import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Users, 
  Shield, 
  TrendingUp,
  MapPin,
  Clock,
  Filter,
  Search,
  Globe,
  Map
} from 'lucide-react';
import GlobeComponent from '../components/dashboard/GlobeComponent';
import RealTimeMap from '../components/dashboard/RealTimeMap';
import EnhancedDisasterFeed from '../components/dashboard/components/DisasterFeed/EnhancedDisasterFeed';
import StatsWidget from '../components/dashboard/StatsWidget';
import { useDisasterFeed } from '../components/dashboard/hooks/useDisasterFeed';
import { 
  mockDisasters,
  mockHumanHealthThreats,
  mockWarConflictThreats,
  mockAnimalHealthThreats,
  mockPlantAgricultureThreats,
  mockEnvironmentalThreats,
  mockTechnologicalThreats
} from '../data/mockData';
import { GeoThreat } from '../components/dashboard/types/disaster';

const Dashboard = () => {
  // Get real-time data from News API
  const API_KEY = import.meta.env.VITE_NEWSDATA_API_KEY;
  const { disasters: realTimeDisasters, loading: apiLoading, error: apiError } = useDisasterFeed(API_KEY);

  // Combine all threats into a single array (keeping this for fallback/testing)
  const allThreats: GeoThreat[] = [
    ...mockDisasters,
    ...mockHumanHealthThreats,
    ...mockWarConflictThreats,
    ...mockAnimalHealthThreats,
    ...mockPlantAgricultureThreats,
    ...mockEnvironmentalThreats,
    ...mockTechnologicalThreats
  ];

  // Use real-time data if available, otherwise fallback to mock data
  const [threats, setThreats] = useState<GeoThreat[]>(realTimeDisasters.length > 0 ? realTimeDisasters : allThreats);
  const [filteredThreats, setFilteredThreats] = useState<GeoThreat[]>(realTimeDisasters.length > 0 ? realTimeDisasters : allThreats);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'globe' | 'map'>('map');

  // Update threats when real-time data changes
  useEffect(() => {
    if (realTimeDisasters.length > 0) {
      setThreats(realTimeDisasters);
    } else if (!apiLoading && !apiError) {
      // Only use mock data if API is not loading and there's no error
      setThreats(allThreats);
    }
  }, [realTimeDisasters, apiLoading, apiError]);

  useEffect(() => {
    let filtered = threats;
    
    if (selectedFilter !== 'all') {
      // Check if it's a category filter or type filter
      if (['natural_disaster', 'human_health', 'war_conflict', 'animal_health', 'plant_agriculture', 'environmental', 'technological'].includes(selectedFilter)) {
        filtered = filtered.filter(t => t.category === selectedFilter);
      } else {
        filtered = filtered.filter(t => t.type === selectedFilter);
      }
    }
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredThreats(filtered);
  }, [threats, selectedFilter, searchTerm]);

  // Calculate real-time statistics
  const activeThreats = threats.filter(t => t.status === 'active').length;
  const totalAffected = threats.reduce((sum, t) => sum + t.impactedCount, 0);
  
  // Calculate safe zones (countries not facing critical problems)
  const criticalThreats = threats.filter(t => t.severity === 'critical').length;
  const totalCountries = 195; // Approximate number of countries
  const safeZones = Math.max(0, totalCountries - criticalThreats);
  
  // Calculate response rate based on resolved vs total threats
  const resolvedThreats = threats.filter(t => t.status === 'resolved').length;
  const responseRate = threats.length > 0 ? ((resolvedThreats / threats.length) * 100).toFixed(1) : '0.0';

  const stats = [
    {
      icon: AlertTriangle,
      label: 'Active Threats',
      value: activeThreats,
      color: 'text-accent-red',
      bgColor: 'bg-accent-red/10',
    },
    {
      icon: Users,
      label: 'People Affected',
      value: totalAffected.toLocaleString(),
      color: 'text-primary-400',
      bgColor: 'bg-primary-400/10',
    },
    {
      icon: Shield,
      label: 'Safe Zones',
      value: safeZones,
      color: 'text-accent-green',
      bgColor: 'bg-accent-green/10',
    },
    {
      icon: TrendingUp,
      label: 'Response Rate',
      value: `${responseRate}%`,
      color: 'text-accent-orange',
      bgColor: 'bg-accent-orange/10',
    },
  ];

  const filterOptions = [
    { value: 'all', label: 'All Threats' },
    { value: 'natural_disaster', label: 'Natural Disasters' },
    { value: 'human_health', label: 'Human Health' },
    { value: 'war_conflict', label: 'War & Conflict' },
    { value: 'animal_health', label: 'Animal Health' },
    { value: 'plant_agriculture', label: 'Agriculture' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'technological', label: 'Technology' },
    { value: 'earthquake', label: 'Earthquakes' },
    { value: 'wildfire', label: 'Wildfires' },
    { value: 'flood', label: 'Floods' },
    { value: 'hurricane', label: 'Hurricanes' },
    { value: 'tornado', label: 'Tornadoes' },
    { value: 'infectious_disease', label: 'Disease Outbreaks' },
    { value: 'cybersecurity', label: 'Cyber Attacks' },
    { value: 'pollution', label: 'Pollution' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20 pb-8 px-4"
    >
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
                Global Threat Dashboard
              </h1>
              <p className="text-gray-300">Real-time monitoring and emergency response coordination for all threat categories</p>
            </div>
            
            {/* Data Source Indicator */}
            <div className="text-right">
              <div className="flex items-center space-x-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${apiLoading ? 'bg-accent-orange animate-pulse' : realTimeDisasters.length > 0 ? 'bg-accent-green' : 'bg-accent-yellow'}`}></div>
                <span className="text-sm text-gray-400">
                  {apiLoading ? 'Loading...' : realTimeDisasters.length > 0 ? 'Live Data' : 'Demo Data'}
                </span>
              </div>
              {realTimeDisasters.length > 0 && (
                <div className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* API Status Banner */}
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-accent-orange/10 border border-accent-orange/30 rounded-lg p-4"
          >
            <div className="flex items-center space-x-2 text-accent-orange">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">
                Using demo data - {apiError}. Real-time data will be available when API is configured.
              </span>
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <StatsWidget key={index} {...stat} index={index} />
          ))}
        </div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search threats by location, type, category, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="pl-10 pr-8 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors appearance-none cursor-pointer min-w-[200px]"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex bg-dark-700 border border-dark-600 rounded-lg p-1">
            <button
              onClick={() => setViewMode('globe')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                viewMode === 'globe' 
                  ? 'bg-primary-400 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>3D Globe</span>
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                viewMode === 'map' 
                  ? 'bg-primary-400 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Map className="w-4 h-4" />
              <span>2D Map</span>
            </button>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Globe/Map Visualization */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2"
          >
            {viewMode === 'globe' ? (
              <GlobeComponent disasters={filteredThreats} />
            ) : (
              <RealTimeMap threats={filteredThreats} />
            )}
          </motion.div>

          {/* Enhanced Disaster Feed - now using apiKey instead of disasters prop */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <EnhancedDisasterFeed />
          </motion.div>
        </div>

        {/* Loading Indicator */}
        {apiLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed bottom-4 right-4 bg-dark-800 border border-dark-600 rounded-lg p-4 shadow-lg"
          >
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin"></div>
              <span className="text-sm text-gray-300">Loading real-time data...</span>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default Dashboard;